import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { campaignService } from '../services/campaignService';
import { detectPlatform, normalizeUrlForEmbedding } from '../utils/mediaUrlUtils';
import { createSafeRedirectUrl, formatUrlForDisplay } from '../utils/redirectUtils';

export const CampaignDetail = () => {
  const { id } = useParams();
  const [campaign, setCampaign] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const contentRef = React.useRef(null);

  const fetchCampaign = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await campaignService.getCampaign(id);
      
      // The API returns the campaign directly
      if (response && response.id) {
        setCampaign(response);
      } else {
        setError('Campaign not found');
      }
    } catch (err) {
      setError('An error occurred while fetching the campaign');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const recordView = useCallback(async () => {
    // Only record view if user is logged in
    if (currentUser && campaign) {
      try {
        await campaignService.recordCampaignView(campaign.id);
      } catch (err) {
        console.error('Failed to record view:', err);
      }
    }
  }, [currentUser, campaign]);

  const renderMediaEmbed = () => {
    if (!campaign.mediaUrl) return null;

    const platform = detectPlatform(campaign.mediaUrl);
    const embedUrl = normalizeUrlForEmbedding(campaign.mediaUrl);

    if (!platform) {
      // Fallback for unsupported platforms
      return (
        <div className="mb-6">
          <h3 className="text-lg font-medium text-brown-900 mb-2">Media</h3>
          <div className="bg-brown-50 border border-brown-200 rounded-lg p-4">
            <p className="text-brown-600 mb-2">Media URL:</p>
            <a 
              href={campaign.mediaUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-brown-600 hover:text-brown-800 break-all"
            >
              {campaign.mediaUrl}
            </a>
          </div>
        </div>
      );
    }

    // Render platform-specific embed
    switch (platform.key) {
      case 'YOUTUBE':
        return (
          <div className="mb-6">
            <h3 className="text-lg font-medium text-brown-900 mb-2">Media (YouTube)</h3>
            <div className="aspect-w-16 aspect-h-9">
              <iframe
                src={embedUrl}
                title={campaign.title}
                className="w-full h-64 md:h-96 rounded-lg"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        );
        
      case 'GOOGLE_DRIVE':
        return (
          <div className="mb-6">
            <h3 className="text-lg font-medium text-brown-900 mb-2">Media (Google Drive)</h3>
            <div className="bg-brown-50 border border-brown-200 rounded-lg p-4">
              <img
                src={embedUrl}
                alt={campaign.title}
                className="max-w-full h-auto rounded-lg mx-auto"
                onError={(e) => {
                  e.target.style.display = 'none';
                  document.getElementById('drive-fallback').style.display = 'block';
                }}
              />
              <div id="drive-fallback" className="hidden text-center">
                <p className="text-brown-600 mb-2">Unable to display image from Google Drive</p>
                <a 
                  href={campaign.mediaUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-brown-600 hover:bg-brown-700 text-white font-bold py-2 px-4 rounded inline-block"
                >
                  Open in Google Drive
                </a>
              </div>
            </div>
          </div>
        );
        
      case 'DROPBOX':
        return (
          <div className="mb-6">
            <h3 className="text-lg font-medium text-brown-900 mb-2">Media (Dropbox)</h3>
            <div className="bg-brown-50 border border-brown-200 rounded-lg p-4">
              {campaign.mediaUrl.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                <img
                  src={embedUrl}
                  alt={campaign.title}
                  className="max-w-full h-auto rounded-lg mx-auto"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    document.getElementById('dropbox-fallback').style.display = 'block';
                  }}
                />
              ) : (
                <video
                  src={embedUrl}
                  controls
                  className="max-w-full h-auto rounded-lg mx-auto"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    document.getElementById('dropbox-fallback').style.display = 'block';
                  }}
                >
                  Your browser does not support the video tag.
                </video>
              )}
              <div id="dropbox-fallback" className="hidden text-center">
                <p className="text-brown-600 mb-2">Unable to display media from Dropbox</p>
                <a 
                  href={campaign.mediaUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-brown-600 hover:bg-brown-700 text-white font-bold py-2 px-4 rounded inline-block"
                >
                  Open in Dropbox
                </a>
              </div>
            </div>
          </div>
        );
        
      case 'INSTAGRAM':
        return (
          <div className="mb-6">
            <h3 className="text-lg font-medium text-brown-900 mb-2">Media (Instagram)</h3>
            <div className="bg-brown-50 border border-brown-200 rounded-lg p-4">
              <blockquote 
                className="instagram-media" 
                data-instgrm-captioned 
                data-instgrm-permalink={campaign.mediaUrl}
                style={{ background: '#FFF', border: 0, borderRadius: '3px', boxShadow: '0 0 1px 0 rgba(0,0,0,0.5),0 1px 10px 0 rgba(0,0,0,0.15)', margin: '1px', maxWidth: '540px', minWidth: '326px', padding: 0, width: '99.375%', WebkitWidth: '-webkit-calc(100% - 2px)', calcWidth: 'calc(100% - 2px)' }}
              >
                <div style={{ padding: '16px' }}>
                  <a href={campaign.mediaUrl} style={{ background: '#FFFFFF', lineHeight: 0, padding: '0 0', textAlign: 'center', textDecoration: 'none', width: '100%' }} target="_blank" rel="noopener noreferrer">
                    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                      <div style={{ backgroundColor: 'black', borderRadius: '50%', flexGrow: 0, height: '40px', marginRight: 14, width: 40 }}></div>
                      <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, justifyContent: 'center' }}>
                        <div style={{ backgroundColor: '#f4f4f4', borderRadius: '4px', flexGrow: 0, height: 14, marginBottom: 6, width: 100 }}></div>
                        <div style={{ backgroundColor: '#f4f4f4', borderRadius: '4px', flexGrow: 0, height: 14, width: 60 }}></div>
                      </div>
                    </div>
                    <div style={{ padding: '19% 0' }}></div>
                    <div style={{ display: 'block', height: 50, margin: '0 auto 12px', width: 50 }}></div>
                    <div style={{ paddingTop: 8 }}>
                      <div style={{ color: '#f4f4f4', lineHeight: 0, paddingTop: 8, textAlign: 'center' }}>
                        <div style={{ display: 'inline-block', fontSize: 13, lineHeight: 16, color: '#989898', fontFamily: 'Helvetica Neue, Helvetica, Arial, sans-serif' }}>View this post on Instagram</div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'row', marginBottom: 14, alignItems: 'center' }}>
                      <div>
                        <div style={{ backgroundColor: '#f4f4f4', borderRadius: '50%', display: 'inline-block', height: 12, marginRight: 6, width: 12 }}></div>
                      </div>
                      <div style={{ lineHeight: 0 }}>
                        <div style={{ backgroundColor: '#f4f4f4', borderRadius: '4px', display: 'inline-block', height: 12, marginRight: 6, width: 100 }}></div>
                      </div>
                      <div style={{ lineHeight: 0 }}>
                        <div style={{ backgroundColor: '#f4f4f4', borderRadius: '4px', display: 'inline-block', height: 12, width: 60 }}></div>
                      </div>
                    </div>
                    <div style={{ paddingTop: 8 }}>
                      <div style={{ color: '#898989', fontFamily: 'Arial, sans-serif', fontSize: 12, lineHeight: 16, textAlign: 'center' }}>
                        <a href={campaign.mediaUrl} style={{ color: '#898989', textDecoration: 'none' }} target="_blank" rel="noopener noreferrer">Loading Instagram content...</a>
                      </div>
                    </div>
                  </a>
                </div>
              </blockquote>
              <div className="text-center mt-4">
                <a 
                  href={campaign.mediaUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-brown-600 hover:bg-brown-700 text-white font-bold py-2 px-4 rounded inline-block"
                >
                  View on Instagram
                </a>
              </div>
            </div>
          </div>
        );
        
      case 'PINTEREST':
        return (
          <div className="mb-6">
            <h3 className="text-lg font-medium text-brown-900 mb-2">Media (Pinterest)</h3>
            <div className="bg-brown-50 border border-brown-200 rounded-lg p-4">
              <a 
                href={embedUrl}
                data-pin-do="embedPin"
                data-pin-width="large"
                target="_blank"
                rel="noopener noreferrer"
                className="block"
              >
                <img 
                  src={`https://i.pinimg.com/564x/4c/6e/4e/4c6e4e5e5e5e5e5e5e5e5e5e5e5e5e5e.jpg`}
                  alt={campaign.title}
                  className="max-w-full h-auto rounded-lg mx-auto"
                />
              </a>
              <div className="text-center mt-4">
                <a 
                  href={campaign.mediaUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-brown-600 hover:bg-brown-700 text-white font-bold py-2 px-4 rounded inline-block"
                >
                  View on Pinterest
                </a>
              </div>
            </div>
          </div>
        );
        
      case 'TIKTOK':
        return (
          <div className="mb-6">
            <h3 className="text-lg font-medium text-brown-900 mb-2">Media (TikTok)</h3>
            <div className="bg-brown-50 border border-brown-200 rounded-lg p-4">
              <blockquote 
                className="tiktok-embed" 
                cite={campaign.mediaUrl} 
                data-video-id={platform.id}
                style={{ maxWidth: '605px', minWidth: '325px' }}
              >
                <section>
                  <a target="_blank" rel="noopener noreferrer" href={campaign.mediaUrl} title={campaign.title}>
                    Loading TikTok content...
                  </a>
                </section>
              </blockquote>
              <div className="text-center mt-4">
                <a 
                  href={campaign.mediaUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-brown-600 hover:bg-brown-700 text-white font-bold py-2 px-4 rounded inline-block"
                >
                  View on TikTok
                </a>
              </div>
            </div>
          </div>
        );
        
      case 'TWITCH':
        return (
          <div className="mb-6">
            <h3 className="text-lg font-medium text-brown-900 mb-2">Media (Twitch)</h3>
            <div className="bg-brown-50 border border-brown-200 rounded-lg p-4">
              <iframe
                src={embedUrl}
                title={campaign.title}
                className="w-full h-64 md:h-96 rounded-lg"
                frameBorder="0"
                allow="autoplay; fullscreen"
                allowFullScreen
              ></iframe>
              <div className="text-center mt-4">
                <a 
                  href={campaign.mediaUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-brown-600 hover:bg-brown-700 text-white font-bold py-2 px-4 rounded inline-block"
                >
                  View on Twitch
                </a>
              </div>
            </div>
          </div>
        );
        
      default:
        return (
          <div className="mb-6">
            <h3 className="text-lg font-medium text-brown-900 mb-2">Media</h3>
            <div className="bg-brown-50 border border-brown-200 rounded-lg p-4">
              <p className="text-brown-600 mb-2">Media URL:</p>
              <a 
                href={campaign.mediaUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-brown-600 hover:text-brown-800 break-all"
              >
                {campaign.mediaUrl}
              </a>
            </div>
          </div>
        );
    }
  };

  // Load embed scripts after component mounts
  useEffect(() => {
    if (campaign && campaign.mediaUrl) {
      const platform = detectPlatform(campaign.mediaUrl);
      
      // Load Instagram embed script
      if (platform && platform.key === 'INSTAGRAM') {
        const script = document.createElement('script');
        script.src = 'https://www.instagram.com/embed.js';
        script.async = true;
        script.onload = () => {
          if (window.instgrm) {
            window.instgrm.Embeds.process();
          }
        };
        document.body.appendChild(script);
      }
      
      // Load Pinterest embed script
      if (platform && platform.key === 'PINTEREST') {
        const script = document.createElement('script');
        script.src = 'https://assets.pinterest.com/js/pinit.js';
        script.async = true;
        script.setAttribute('data-pin-build', 'async');
        document.body.appendChild(script);
      }
      
      // Load TikTok embed script
      if (platform && platform.key === 'TIKTOK') {
        const script = document.createElement('script');
        script.src = 'https://www.tiktok.com/embed.js';
        script.async = true;
        document.body.appendChild(script);
      }
    }
  }, [campaign]);

  useEffect(() => {
    if (id) {
      fetchCampaign();
    }
  }, [id, fetchCampaign]);

  useEffect(() => {
    if (campaign) {
      recordView();
    }
  }, [campaign, recordView]);

  // Check if content is scrollable and show scroll indicator
  useEffect(() => {
    const checkScrollable = () => {
      if (contentRef.current) {
        const { scrollHeight, clientHeight, scrollTop } = contentRef.current;
        const isScrollable = scrollHeight > clientHeight;
        const isAtTop = scrollTop < 50;
        setShowScrollIndicator(isScrollable && isAtTop);
      }
    };

    checkScrollable();

    const contentElement = contentRef.current;
    if (contentElement) {
      contentElement.addEventListener('scroll', checkScrollable);
      window.addEventListener('resize', checkScrollable);

      return () => {
        contentElement.removeEventListener('scroll', checkScrollable);
        window.removeEventListener('resize', checkScrollable);
      };
    }
  }, [campaign]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full text-brown-600" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <p className="mt-2 text-brown-600">Loading campaign...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-md p-6 mt-8">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          <p>{error}</p>
        </div>
        <div className="mt-4">
          <Link to="/" className="text-brown-600 hover:text-brown-800">
            Back to Campaigns
          </Link>
        </div>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-md p-6 mt-8">
        <div className="text-center py-12">
          <h3 className="text-lg font-medium text-brown-900 mb-2">Campaign not found</h3>
          <p className="text-brown-600 mb-6">The campaign you're looking for doesn't exist.</p>
          <Link to="/" className="bg-brown-600 hover:bg-brown-700 text-white font-bold py-2 px-4 rounded">
            Back to Campaigns
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full p-4 overflow-hidden flex flex-col main-container" style={{ backgroundColor: 'transparent' }}>
      <div className="mb-3">
        <button
          onClick={() => navigate(-1)}
          className="text-brown-600 hover:text-brown-800 flex items-center text-sm"
        >
          ← Back
        </button>
      </div>
      
      <div className="flex-1 overflow-y-auto relative" ref={contentRef}>
        <div className="bg-white rounded-lg shadow-md p-4 h-full">
          <div className="mb-4">
            <h1 className="text-xl font-bold text-brown-800 mb-2">{campaign.title}</h1>
            
            <div className="flex items-center text-xs text-brown-500 mb-2">
              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
              </svg>
              Created: {formatDate(campaign.createdAt)}
            </div>
            
            <div className="prose max-w-none">
              <p className="text-brown-700 whitespace-pre-wrap text-sm">{campaign.description}</p>
            </div>
          </div>
          
          <div className="mb-4">
            {renderMediaEmbed()}
          </div>
          
          {campaign.targetLink && (
            <div className="mb-4">
              <h3 className="text-sm font-medium text-brown-900 mb-1">Target Link</h3>
              <a
                href={createSafeRedirectUrl(campaign.targetLink)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-brown-600 hover:text-brown-800 break-all inline-flex items-center text-xs"
              >
                {formatUrlForDisplay(campaign.targetLink, 60)}
                <svg className="w-3 h-3 ml-1" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
                  <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
                </svg>
              </a>
            </div>
          )}
          
          <div className="border-t pt-3">
            <div className="flex items-center justify-between">
              <div className="text-xs text-brown-500">
                Status: <span className="font-medium">{campaign.status}</span>
              </div>
              
              {currentUser && campaign.owner_uid === currentUser.uid && (
                <div className="space-x-2">
                  <Link
                    to={`/campaigns/${campaign.id}/edit`}
                    className="bg-brown-600 hover:bg-brown-700 text-white font-bold py-1 px-3 rounded text-xs"
                  >
                    Edit Campaign
                  </Link>
                </div>
              )}
              
              {!currentUser && (
                <div className="space-x-2">
                  <Link
                    to="/login"
                    className="bg-brown-600 hover:bg-brown-700 text-white font-bold py-1 px-3 rounded text-xs"
                  >
                    Login to Interact
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        {showScrollIndicator && (
          <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 z-10 pointer-events-none">
            <div className="bg-brown-600 text-white px-4 py-2 rounded-full shadow-lg flex items-center space-x-2 animate-bounce">
              <span className="text-sm font-medium">Scroll for more</span>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CampaignDetail;